import clsx from "clsx";
import { AnimatePresence, motion, type PanInfo } from "motion/react";
import type { SyntheticEvent } from "react";
import {
  FiBell,
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiGrid,
  FiHome,
  FiList,
  FiMonitor,
  FiMusic,
  FiRefreshCw,
  FiSearch,
  FiSettings,
  FiShieldOff,
  FiWatch,
} from "react-icons/fi";
import { MenuItem } from "@/app/layouts/MenuItem";
import {
  type SideMenuPresentation,
  useSideMenuPresenter,
} from "@/app/layouts/sideMenuPresenter";
import noImage from "@/assets/img/no-image.svg";
import {
  getImageUrl,
  getThreadTitle,
  isThreadExpired,
} from "@/entities/thread";
import { AnnouncementNavLabel } from "@/features/announce/components/AnnouncementNavLabel";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { HistoryModeToggle } from "@/features/history/components/HistoryModeToggle";
import type { HistoryMode } from "@/features/history/historyMode";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { TextLink } from "@/shared/ui/navigation/TextLink";
import { DialogCloseButton } from "@/shared/ui/overlay/DialogCloseButton";
import { getDialogAccessibilityProps } from "@/shared/ui/overlay/dialogAccessibility";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const SideMenu: React.FunctionComponent<Props> = ({
  isOpen,
  onClose,
}: Props) => {
  const presentation = useSideMenuPresenter({ isOpen, onClose });
  const { node, ...viewProps } = presentation;

  return (
    <ComponentScope node={node}>
      <SideMenuView {...viewProps} />
    </ComponentScope>
  );
};

interface SideMenuViewProps extends Omit<SideMenuPresentation, "node"> {}

export const SideMenuView: React.FunctionComponent<SideMenuViewProps> = ({
  isOpen,
  dialogRef,
  catalogLink,
  boardRulesHref,
  historyHref,
  forcePcUrl,
  historyMode,
  historyModeText,
  historyItemsCount,
  historyThreadsCount,
  historyEntries,
  isHistoryError,
  isHistoryFetching,
  jukeboxEnabled,
  hasUnreadAnnouncements,
  onEvent,
}: SideMenuViewProps) => {
  const requestClose = (): void => {
    onEvent({ type: "side-menu.close-requested" });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={requestClose}
          />
          <motion.aside
            ref={dialogRef}
            tabIndex={-1}
            {...getDialogAccessibilityProps("side-menu-title", true)}
            className="fixed top-0 left-0 bottom-0 w-72 pl-[env(safe-area-inset-left)] bg-card z-50 flex flex-col overflow-y-auto overscroll-contain"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={{ left: 0.8, right: 0 }}
            onDragEnd={(_e: unknown, info: PanInfo): void => {
              onEvent({
                type: "side-menu.drag-ended",
                offsetX: info.offset.x,
                velocityX: info.velocity.x,
              });
            }}
          >
            {/* ヘッダー */}
            <header className="flex items-center justify-between px-4 pt-[env(safe-area-inset-top)] border-b border-border bg-card sticky top-0 w-full flex-none basis-14">
              <h1
                id="side-menu-title"
                className="text-lg font-bold text-foreground"
              >
                メニュー
              </h1>
              <DialogCloseButton
                onClose={requestClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted"
              />
            </header>

            {/* メニュー項目 */}
            <nav className="p-2 border-b border-border">
              {catalogLink.kind === "href" ? (
                <MenuItem
                  icon={<FiGrid className="w-5 h-5" />}
                  href={catalogLink.href}
                  onClick={requestClose}
                  isInternal
                >
                  カタログ
                </MenuItem>
              ) : (
                <MenuItem
                  icon={<FiGrid className="w-5 h-5" />}
                  to={catalogLink.to}
                  onClick={requestClose}
                  isInternal
                >
                  カタログ
                </MenuItem>
              )}
              <MenuItem
                icon={<FiBell className="w-5 h-5" aria-hidden="true" />}
                to="/announcements"
                onClick={requestClose}
                isInternal
                className="min-h-11"
              >
                <AnnouncementNavLabel isNew={hasUnreadAnnouncements} />
              </MenuItem>
              <MenuItem
                icon={<FiSettings className="w-5 h-5" />}
                to="/settings"
                onClick={requestClose}
                isInternal
              >
                設定
              </MenuItem>
              <MenuItem
                icon={<FiWatch className="w-5 h-5" />}
                to="/archive"
                onClick={requestClose}
              >
                過去ログ
              </MenuItem>
              <MenuItem
                icon={<FiHome className="w-5 h-5" />}
                href="/"
                isInternal
                onClick={requestClose}
              >
                ホーム
              </MenuItem>

              <details className="group [&>:not(summary)]:ml-2 [&>:not(summary)]:w-[calc(100%-0.5rem)]">
                <summary className="grid min-h-11 cursor-pointer list-none grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-lg px-3 py-2 text-foreground transition-colors hover:bg-muted [&::-webkit-details-marker]:hidden">
                  <FiChevronDown
                    className="h-5 w-5 transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                  すべて表示
                </summary>
                <MenuItem
                  icon={<FiList className="w-5 h-5" />}
                  to="/board"
                  onClick={requestClose}
                  isInternal
                >
                  リスト
                </MenuItem>
                <MenuItem
                  icon={<FiSearch className="w-5 h-5" />}
                  to="/search"
                  onClick={requestClose}
                  isInternal
                >
                  投稿検索
                </MenuItem>
                {jukeboxEnabled && (
                  <MenuItem
                    icon={<FiMusic className="w-5 h-5" />}
                    to="/jukebox"
                    onClick={requestClose}
                    isInternal
                  >
                    ジュークボックス
                  </MenuItem>
                )}
                <MenuItem
                  icon={<FiShieldOff className="w-5 h-5" />}
                  href={boardRulesHref}
                  isInternal
                >
                  利用規約
                </MenuItem>
                <MenuItem
                  icon={<FiClock className="w-5 h-5" />}
                  href={historyHref}
                  isInternal
                  onClick={requestClose}
                >
                  履歴
                </MenuItem>
                <MenuItem
                  icon={<FiMonitor className="w-5 h-5" />}
                  href={forcePcUrl}
                  isInternal
                >
                  PC版に切替
                </MenuItem>
              </details>
            </nav>

            {/* 履歴 */}
            <section aria-label="履歴" className="flex-1">
              <header className="flex items-center justify-between px-4 py-2 text-sm font-medium text-muted-foreground bg-card sticky top-14 w-full">
                <TextLink
                  to="/history/$mode"
                  params={{ mode: "viewed" }}
                  onClick={requestClose}
                  variant="muted"
                  aria-label="履歴ページを開く"
                  className="inline-flex items-center gap-0.5"
                >
                  履歴
                  <FiChevronRight className="w-4 h-4" aria-hidden="true" />
                </TextLink>
                {historyItemsCount > 0 && (
                  <button
                    type="button"
                    onClick={(): void => {
                      onEvent({ type: "side-menu.history-refresh-requested" });
                    }}
                    disabled={isHistoryFetching}
                    aria-label={`${historyModeText}を再取得`}
                    className="rounded p-1 hover:bg-muted disabled:opacity-50"
                  >
                    <FiRefreshCw
                      className={isHistoryFetching ? "animate-spin" : ""}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </header>
              <HistoryModeToggle
                mode={historyMode}
                onChange={(mode: HistoryMode): void => {
                  onEvent({
                    type: "side-menu.history-mode-selected",
                    mode,
                  });
                }}
                className="side-menu-history-mode-toggle"
              />
              {historyItemsCount === 0 ? (
                <p className="px-4 py-8 text-center text-muted-foreground text-sm">
                  {historyModeText}がありません
                </p>
              ) : isHistoryError ? (
                <p className="px-4 py-8 text-center text-muted-foreground text-sm">
                  {historyModeText}を取得できませんでした
                </p>
              ) : historyThreadsCount === 0 && isHistoryFetching ? (
                <p className="px-4 py-8 text-center text-muted-foreground text-sm">
                  {historyModeText}を読み込み中...
                </p>
              ) : (
                <ol
                  aria-label={historyModeText}
                  className="m-0 list-none space-y-1 px-2 pb-4"
                >
                  {historyEntries.map(
                    ({ key, thread, unreadCount }): React.ReactElement => (
                      <li key={key}>
                        <CatalogThreadLink
                          thread={thread}
                          onClick={requestClose}
                          className={clsx(
                            "grid grid-cols-[3rem_minmax(0,1fr)] grid-rows-2 items-center gap-x-3 rounded-lg p-2 transition-colors hover:bg-muted",
                            isThreadExpired(thread) && "catalog-item-expired",
                          )}
                        >
                          <img
                            src={getImageUrl(thread.opPost.attachment, false)}
                            alt=""
                            className="row-span-2 h-12 w-12 rounded bg-muted object-cover"
                            onError={(
                              event: SyntheticEvent<HTMLImageElement>,
                            ): void => {
                              event.currentTarget.src = noImage;
                            }}
                          />
                          <b className="block min-w-0 truncate text-sm font-normal text-foreground">
                            {getThreadTitle(thread)}
                          </b>
                          <small className="block text-xs text-muted-foreground">
                            {thread.replyCount}レス
                            {unreadCount && (
                              <strong className="mx-1 font-normal text-primary">
                                未読{unreadCount}レス
                              </strong>
                            )}
                          </small>
                        </CatalogThreadLink>
                      </li>
                    ),
                  )}
                </ol>
              )}
            </section>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
